import { useState } from 'react';
import { Download, FileSpreadsheet, X } from 'lucide-react';
// Using ExcelJS for full style support
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import { Button } from '../ui/button.tsx';
import {
  getCOATemplates,
  getCOATemplateById,
  getReleaseCompareRowsByBatch,
  type COATemplateDto,
  type COATemplateItemDto,
  type CompareRowDto,
} from '../../../services/coaReleaseService.ts';

interface ExportComparisonResult {
  parameter: string;
  testMethod: string;
  uom: string;
  testValueRaw: string;
}

interface ReleaseTaskExportProps {
  batchNumber: string;
  comparisonResults: ExportComparisonResult[];
  disabled?: boolean;
}

interface PreviewCell {
  key: string;
  value: string;
  colSpan: number;
  className: string;
}

interface PreviewRow {
  key: string;
  cells: PreviewCell[];
}

interface BuiltCoAExport {
  workbook: XLSX.WorkBook;
  xlsxBytes: Uint8Array;
}

const COA_TEMPLATE_SHEET_NAME = 'CoA Template';
const DATA_START_ROW_INDEX = 8;
const EXPORT_RANGE = 'A1:D96';
// XML namespace constants removed (not used for programmatic generation)
// Package/relationship XML constants removed (not used for programmatic generation)
const XLSX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const TITLE_FONT_SIZE = 16;
const CLASSIFICATION_FONT_SIZE = 14;
const CLASSIFICATION_FILL_COLOR = 'FF193767';
const EXPORT_COLUMN_WIDTHS = [45, 40, 40, 45] as const;

const cleanText = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  const text = String(value).trim();
  return text.toUpperCase() === '#VALUE!' ? '' : text;
};

const formatDateOnly = (value: unknown): string => {
  const text = cleanText(value);
  if (!text) return '';
  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return text;
  return date.toISOString().slice(0, 10).replace(/-/g, '/');
};

const normalizeKey = (value: unknown): string =>
  cleanText(value).toLowerCase().replace(/\s+/g, '').replace(/[^\p{L}\p{N}%]+/gu, '');

const buildFullMatchKey = (parameter: unknown, testMethod: unknown, uom: unknown) =>
  `${normalizeKey(parameter)}|${normalizeKey(testMethod)}|${normalizeKey(uom)}`;

const buildMethodMatchKey = (parameter: unknown, testMethod: unknown) =>
  `${normalizeKey(parameter)}|${normalizeKey(testMethod)}`;

const getTemplateItems = (template: COATemplateDto | null): COATemplateItemDto[] =>
  Array.isArray(template?.items) ? template.items : [];

const getCellDisplayText = (sheet: XLSX.WorkSheet, rowIndex: number, columnIndex: number): string => {
  const cell = sheet[XLSX.utils.encode_cell({ r: rowIndex, c: columnIndex })] as XLSX.CellObject | undefined;
  if (!cell) return '';
  if (typeof cell.w === 'string' && cell.w.trim()) return cell.w.trim();
  return cleanText(cell.v);
};

const appendLabelValue = (label: string, value: unknown) => {
  const text = cleanText(value);
  return text ? `${label} ${text}` : label;
};

const hasMeaningfulItemContent = (item: COATemplateItemDto) =>
  [item.classification, item.parameter, item.uom, item.testMethod]
    .some((value) => cleanText(value).length > 0);

// Helper function removed - merges now handled directly in buildCoATemplateWorkbook

const buildTestResultLookup = (compareRows: CompareRowDto[], fallbackRows: ExportComparisonResult[]) => {
  const full = new Map<string, string>();
  const method = new Map<string, string>();
  const parameter = new Map<string, string>();

  const add = (parameterValue: unknown, testMethodValue: unknown, uomValue: unknown, testResultValue: unknown) => {
    const value = cleanText(testResultValue);
    const fullKey = buildFullMatchKey(parameterValue, testMethodValue, uomValue);
    const methodKey = buildMethodMatchKey(parameterValue, testMethodValue);
    const parameterKey = normalizeKey(parameterValue);

    if (fullKey !== '||' && !full.has(fullKey)) full.set(fullKey, value);
    if (methodKey !== '|' && !method.has(methodKey)) method.set(methodKey, value);
    if (parameterKey && !parameter.has(parameterKey)) parameter.set(parameterKey, value);
  };

  compareRows.forEach((item) => add(item.parameter, item.testMethod, item.uom, item.testValue));
  fallbackRows.forEach((item) => add(item.parameter, item.testMethod, item.uom, item.testValueRaw));

  return { full, method, parameter };
};

const getTestResultValue = (
  item: COATemplateItemDto,
  lookup: ReturnType<typeof buildTestResultLookup>
) => {
  const fullKey = buildFullMatchKey(item.parameter, item.testMethod, item.uom);
  const methodKey = buildMethodMatchKey(item.parameter, item.testMethod);
  const parameterKey = normalizeKey(item.parameter);

  return (
    lookup.full.get(fullKey) ??
    lookup.method.get(methodKey) ??
    lookup.parameter.get(parameterKey) ??
    ''
  );
};

const isCoATableHeaderRow = (sheet: XLSX.WorkSheet, rowIndex: number): boolean => {
  const uom = normalizeKey(getCellDisplayText(sheet, rowIndex, 1));
  const testResult = normalizeKey(getCellDisplayText(sheet, rowIndex, 2));
  const testMethod = normalizeKey(getCellDisplayText(sheet, rowIndex, 3));

  return uom.includes('uom') && testResult.includes('testresult') && testMethod.includes('testmethod');
};

const isCoAClassificationRow = (sheet: XLSX.WorkSheet, rowIndex: number): boolean => {
  if (rowIndex < DATA_START_ROW_INDEX) return false;

  const classification = cleanText(getCellDisplayText(sheet, rowIndex, 0));
  const uom = normalizeKey(getCellDisplayText(sheet, rowIndex, 1));
  const testResult = normalizeKey(getCellDisplayText(sheet, rowIndex, 2));
  const testMethod = normalizeKey(getCellDisplayText(sheet, rowIndex, 3));

  return Boolean(classification) && uom.includes('uom') && testResult.includes('testresult') && testMethod.includes('testmethod');
};

const isCoAClassificationRowFromUpdates = (updates: Record<string, string>, rowIndex: number): boolean => {
  if (rowIndex < DATA_START_ROW_INDEX) return false;

  const classification = cleanText(updates[XLSX.utils.encode_cell({ r: rowIndex, c: 0 })]);
  const uom = normalizeKey(updates[XLSX.utils.encode_cell({ r: rowIndex, c: 1 })]);
  const testResult = normalizeKey(updates[XLSX.utils.encode_cell({ r: rowIndex, c: 2 })]);
  const testMethod = normalizeKey(updates[XLSX.utils.encode_cell({ r: rowIndex, c: 3 })]);

  return Boolean(classification) && uom.includes('uom') && testResult.includes('testresult') && testMethod.includes('testmethod');
};

const setCellUpdate = (updates: Record<string, string>, address: string, value: unknown) => {
  updates[address] = cleanText(value);
};

const syncTemplateMasterRows = (updates: Record<string, string>, template: COATemplateDto) => {
  setCellUpdate(updates, 'A1', 'CERTIFICATE OF ANALYSIS');
  setCellUpdate(updates, 'A3', 'Non-Initial Shipment');

  setCellUpdate(updates, 'A5', 'Product Description:');
  setCellUpdate(updates, 'B5', template.productDescription);
  setCellUpdate(updates, 'C5', 'Manufacture Date:');
  setCellUpdate(updates, 'D5', formatDateOnly(template.manufactureDate));

  setCellUpdate(updates, 'A6', 'Spec Number:');
  setCellUpdate(updates, 'B6', template.specNumber);
  setCellUpdate(updates, 'C6', 'Expiry Date:');
  setCellUpdate(updates, 'D6', formatDateOnly(template.expiryDate));

  setCellUpdate(updates, 'A7', 'Batch Number:');
  setCellUpdate(updates, 'B7', template.batchNumber);
};

const syncTemplateDetailRows = (
  sheet: XLSX.WorkSheet,
  updates: Record<string, string>,
  template: COATemplateDto,
  compareRows: CompareRowDto[],
  fallbackRows: ExportComparisonResult[],
  footerStartRow: number
) => {
  const lookup = buildTestResultLookup(compareRows, fallbackRows);
  const templateItems = getTemplateItems(template).filter(hasMeaningfulItemContent);

  type ItemGroup = {
    label: string;
    items: COATemplateItemDto[];
  };

  const groupedItems = new Map<string, ItemGroup>();
  const itemGroupsInOrder: ItemGroup[] = [];

  templateItems.forEach((item) => {
    const rawClassification = cleanText(item.classification) || 'General';
    let group = groupedItems.get(rawClassification);

    if (!group) {
      group = { label: rawClassification, items: [] };
      groupedItems.set(rawClassification, group);
      itemGroupsInOrder.push(group);
    }

    group.items.push(item);
  });

  const detailRowIndexes: number[] = [];
  for (let rowIndex = DATA_START_ROW_INDEX; rowIndex < footerStartRow; rowIndex++) {
    detailRowIndexes.push(rowIndex);
  }

  const writeCategoryHeaderRow = (rowIndex: number, classificationLabel: string) => {
    setCellUpdate(updates, XLSX.utils.encode_cell({ r: rowIndex, c: 0 }), classificationLabel);
    setCellUpdate(updates, XLSX.utils.encode_cell({ r: rowIndex, c: 1 }), 'UOM 单位');
    setCellUpdate(updates, XLSX.utils.encode_cell({ r: rowIndex, c: 2 }), 'Test Result 检测结果');
    setCellUpdate(updates, XLSX.utils.encode_cell({ r: rowIndex, c: 3 }), 'Test Method 检测方法');
  };

  const writeItemRow = (rowIndex: number, item: COATemplateItemDto) => {
    setCellUpdate(updates, XLSX.utils.encode_cell({ r: rowIndex, c: 0 }), item.parameter);
    setCellUpdate(updates, XLSX.utils.encode_cell({ r: rowIndex, c: 1 }), item.uom);
    setCellUpdate(updates, XLSX.utils.encode_cell({ r: rowIndex, c: 2 }), getTestResultValue(item, lookup));
    setCellUpdate(updates, XLSX.utils.encode_cell({ r: rowIndex, c: 3 }), item.testMethod);
  };

  let rowPointer = 0;

  for (const group of itemGroupsInOrder) {
    if (!group.items.length) continue;

    if (rowPointer >= detailRowIndexes.length) break;

    const categoryHeaderRowIndex = detailRowIndexes[rowPointer++];
    writeCategoryHeaderRow(categoryHeaderRowIndex, group.label);

    for (const item of group.items) {
      if (rowPointer >= detailRowIndexes.length) break;
      const dataRowIndex = detailRowIndexes[rowPointer++];
      writeItemRow(dataRowIndex, item);
    }
  }
};

const syncTemplateFooterRows = (updates: Record<string, string>, template: COATemplateDto, footerStartRow: number) => {
  const descriptionLines = cleanText(template.description)
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  const descriptionRows = descriptionLines.length > 5
    ? [...descriptionLines.slice(0, 4), descriptionLines.slice(4).join('\n')]
    : descriptionLines;

  for (let offset = 0; offset < 5; offset++) {
    const rowNumber = footerStartRow + offset + 1;
    setCellUpdate(updates, `A${rowNumber}`, descriptionRows[offset] || '');
  }

  const metadataStartRow = footerStartRow + 7;
  setCellUpdate(updates, `A${metadataStartRow}`, appendLabelValue('Prepared by:', template.preparedBy));
  setCellUpdate(updates, `C${metadataStartRow}`, appendLabelValue('Released by:', template.releaseBy));
  setCellUpdate(updates, `A${metadataStartRow + 1}`, appendLabelValue('Title:', template.preparedByTitle));
  setCellUpdate(updates, `C${metadataStartRow + 1}`, appendLabelValue('Title:', template.releaseByTitle));
  setCellUpdate(updates, `A${metadataStartRow + 2}`, appendLabelValue('Signature:', template.preparedBySignature));
  setCellUpdate(updates, `C${metadataStartRow + 2}`, appendLabelValue('Signature:', template.releaseBySignature));
  setCellUpdate(updates, `A${metadataStartRow + 3}`, '');
  setCellUpdate(updates, `C${metadataStartRow + 3}`, '');
  setCellUpdate(updates, `A${metadataStartRow + 5}`, appendLabelValue('Date:', formatDateOnly(template.preparedByDate)));
  setCellUpdate(updates, `C${metadataStartRow + 5}`, appendLabelValue('Date:', formatDateOnly(template.releaseByDate)));
};

const syncDisplayedFormulaValues = (sheet: XLSX.WorkSheet, updates: Record<string, string>) => {
  const range = XLSX.utils.decode_range(sheet['!ref'] || EXPORT_RANGE);

  for (let rowIndex = range.s.r; rowIndex <= range.e.r; rowIndex++) {
    for (let columnIndex = range.s.c; columnIndex <= range.e.c; columnIndex++) {
      const address = XLSX.utils.encode_cell({ r: rowIndex, c: columnIndex });
      const cell = sheet[address] as XLSX.CellObject | undefined;
      if (!cell?.f || Object.prototype.hasOwnProperty.call(updates, address)) continue;

      setCellUpdate(updates, address, getCellDisplayText(sheet, rowIndex, columnIndex));
    }
  }
};

const buildTemplateCellUpdates = (
  sheet: XLSX.WorkSheet,
  template: COATemplateDto,
  compareRows: CompareRowDto[],
  fallbackRows: ExportComparisonResult[],
  footerStartRow: number
) => {
  const updates: Record<string, string> = {};

  syncTemplateMasterRows(updates, template);
  syncTemplateDetailRows(sheet, updates, template, compareRows, fallbackRows, footerStartRow);
  syncTemplateFooterRows(updates, template, footerStartRow);
  syncDisplayedFormulaValues(sheet, updates);

  return updates;
};

const getDynamicExportRowCount = (template: COATemplateDto) => {
  const itemCount = getTemplateItems(template).filter(hasMeaningfulItemContent).length;
  const groupCount = new Set(
    getTemplateItems(template)
      .filter(hasMeaningfulItemContent)
      .map((item) => cleanText(item.classification) || 'General')
  ).size;
  const detailRows = itemCount + groupCount;
  const footerRows = 12;
  const minimumRows = 10;
  return Math.max(minimumRows, 8 + detailRows + footerRows);
};

// getTargetSheetName removed — not needed for programmatic generation

const buildCoATemplateWorkbook = async (
  template: COATemplateDto,
  compareRows: CompareRowDto[],
  fallbackRows: ExportComparisonResult[]
): Promise<BuiltCoAExport> => {
  // Build data structure
  const updates = buildTemplateCellUpdates(
    {} as XLSX.WorkSheet,
    template,
    compareRows,
    fallbackRows,
    Math.max(DATA_START_ROW_INDEX + 1, getDynamicExportRowCount(template) - 12)
  );

  const totalRows = getDynamicExportRowCount(template);
  const footerStartRow = Math.max(DATA_START_ROW_INDEX + 1, totalRows - 12);

  // Create ExcelJS workbook with styles
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(COA_TEMPLATE_SHEET_NAME);

  // Set column widths
  worksheet.columns = [
    { width: EXPORT_COLUMN_WIDTHS[0] },
    { width: EXPORT_COLUMN_WIDTHS[1] },
    { width: EXPORT_COLUMN_WIDTHS[2] },
    { width: EXPORT_COLUMN_WIDTHS[3] },
  ];

  worksheet.views = [
    {
      state: 'normal',
      zoomScale: 90,
      showGridLines: false,
    },
  ];

  worksheet.pageSetup = {
    orientation: 'landscape',
    fitToPage: true,
    fitToWidth: 1,
    fitToHeight: 1,
    paperSize: 9,
    horizontalDpi: 300,
    verticalDpi: 300,
    showGridLines: false,
  };

  (worksheet as any).pageMargins = {
    left: 0.3,
    right: 0.3,
    top: 0.4,
    bottom: 0.4,
    header: 0.2,
    footer: 0.2,
  };

  // Initialize all rows with data
  for (let rowIndex = 1; rowIndex <= totalRows; rowIndex++) {
    const row: (string | null)[] = [];
    for (let colIndex = 1; colIndex <= 4; colIndex++) {
      const address = XLSX.utils.encode_cell({ r: rowIndex - 1, c: colIndex - 1 });
      row.push(updates[address] || '');
    }
    worksheet.addRow(row);
  }

  // Apply styles and formatting
  for (let rowIndex = 1; rowIndex <= totalRows; rowIndex++) {
    const excelRow = worksheet.getRow(rowIndex);
    const rowIdx0Based = rowIndex - 1;

    // Helper: Check if this is a header row
    const isHeaderRow = () => {
      const colB = cleanText(updates[XLSX.utils.encode_cell({ r: rowIdx0Based, c: 1 })] || '').toLowerCase();
      const colC = cleanText(updates[XLSX.utils.encode_cell({ r: rowIdx0Based, c: 2 })] || '').toLowerCase();
      const colD = cleanText(updates[XLSX.utils.encode_cell({ r: rowIdx0Based, c: 3 })] || '').toLowerCase();
      return colB.includes('uom') && colC.includes('testresult') && colD.includes('testmethod');
    };

    const commonBorder = {
      top: { style: 'thin' as const, color: { argb: 'FFCBD5E0' } },
      bottom: { style: 'thin' as const, color: { argb: 'FFCBD5E0' } },
      left: { style: 'thin' as const, color: { argb: 'FFCBD5E0' } },
      right: { style: 'thin' as const, color: { argb: 'FFCBD5E0' } },
    };

    for (let colIndex = 1; colIndex <= 4; colIndex++) {
      const cell = excelRow.getCell(colIndex);

      if (rowIdx0Based === 0) {
        // Title row: dark background, white text, center, bold
        cell.fill = {
          type: 'pattern' as const,
          pattern: 'solid' as const,
          fgColor: { argb: CLASSIFICATION_FILL_COLOR },
        };
        cell.font = { color: { argb: 'FFFFFFFF' }, bold: true, size: TITLE_FONT_SIZE };
        cell.alignment = { horizontal: 'center' as const, vertical: 'middle' as const, wrapText: true };
        cell.border = commonBorder as any;
      } else if (rowIdx0Based === 2) {
        // Subtitle row: light gray background
        cell.fill = {
          type: 'pattern' as const,
          pattern: 'solid' as const,
          fgColor: { argb: 'FFF3F4F6' },
        };
        cell.font = { color: { argb: 'FF0F172A' }, bold: true };
        cell.alignment = { horizontal: 'center' as const, vertical: 'middle' as const, wrapText: true };
        cell.border = undefined as any;
      } else if (rowIdx0Based >= DATA_START_ROW_INDEX && rowIdx0Based < footerStartRow) {
        // Item table rows
        if (isCoAClassificationRowFromUpdates(updates, rowIdx0Based)) {
          // Classification row: same dark styling as the title
          cell.fill = {
            type: 'pattern' as const,
            pattern: 'solid' as const,
            fgColor: { argb: CLASSIFICATION_FILL_COLOR },
          };
          cell.font = { color: { argb: 'FFFFFFFF' }, bold: true, size: CLASSIFICATION_FONT_SIZE };
          cell.alignment = { horizontal: 'center' as const, vertical: 'middle' as const, wrapText: true };
          cell.border = commonBorder as any;
        } else if (isHeaderRow()) {
          // Header row: dark background, white text
          cell.fill = {
            type: 'pattern' as const,
            pattern: 'solid' as const,
            fgColor: { argb: 'FF374151' },
          };
          cell.font = { color: { argb: 'FFFFFFFF' }, bold: true };
          cell.alignment = { horizontal: 'left' as const, vertical: 'middle' as const, wrapText: true };
          cell.border = commonBorder as any;
        } else {
          // Data rows
          cell.fill = {
            type: 'pattern' as const,
            pattern: 'solid' as const,
            fgColor: { argb: 'FFFFFFFF' },
          };
          cell.font = { color: { argb: 'FF0F172A' } };
          cell.alignment = { horizontal: 'left' as const, vertical: 'top' as const, wrapText: true };
          cell.border = commonBorder as any;
        }
      } else if (rowIdx0Based >= footerStartRow && rowIdx0Based <= footerStartRow + 4) {
        // Description rows
        cell.fill = {
          type: 'pattern' as const,
          pattern: 'solid' as const,
          fgColor: { argb: 'FFFFFFFF' },
        };
        cell.font = { color: { argb: 'FF0F172A' } };
        cell.alignment = { horizontal: 'left' as const, vertical: 'top' as const, wrapText: true };
        cell.border = undefined as any;
      } else if (rowIdx0Based >= footerStartRow + 6) {
        // Metadata rows
        cell.fill = {
          type: 'pattern' as const,
          pattern: 'solid' as const,
          fgColor: { argb: 'FFFFFFFF' },
        };
        cell.font = { color: { argb: 'FF0F172A' }, bold: true };
        cell.alignment = { horizontal: 'left' as const, vertical: 'middle' as const, wrapText: true };
        cell.border = undefined as any;
      } else {
        // Master rows (5-7)
        cell.fill = {
          type: 'pattern' as const,
          pattern: 'solid' as const,
          fgColor: { argb: 'FFFFFFFF' },
        };
        cell.font = { color: { argb: 'FF0F172A' } };
        cell.alignment = { horizontal: 'left' as const, vertical: 'middle' as const, wrapText: true };
        cell.border = undefined as any;
      }
    }
  }

  // Apply merges
  worksheet.mergeCells(`A1:D1`);
  worksheet.mergeCells(`A3:D3`);
  for (let rowIndex = footerStartRow + 1; rowIndex <= footerStartRow + 5; rowIndex++) {
    worksheet.mergeCells(`A${rowIndex}:D${rowIndex}`);
  }

  // Export to buffer
  const excelBuffer = await workbook.xlsx.writeBuffer();
  const xlsxBytes = new Uint8Array(excelBuffer as ArrayBuffer);

  // Also create XLSX workbook for preview rendering
  const previewWorkbook = XLSX.utils.book_new();
  const previewSheet = XLSX.utils.aoa_to_sheet(
    Array.from({ length: totalRows }, (_, r) =>
      Array.from({ length: 4 }, (_, c) => updates[XLSX.utils.encode_cell({ r, c })] || '')
    )
  );
  previewSheet['!ref'] = `A1:D${totalRows}`;
  previewSheet['!cols'] = EXPORT_COLUMN_WIDTHS.map((width) => ({ wch: width }));
  previewSheet['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 3 } },
    { s: { r: 2, c: 0 }, e: { r: 2, c: 3 } },
    ...Array.from({ length: 5 }, (_, i) => ({
      s: { r: footerStartRow + i, c: 0 },
      e: { r: footerStartRow + i, c: 3 },
    })),
  ];
  XLSX.utils.book_append_sheet(previewWorkbook, previewSheet, COA_TEMPLATE_SHEET_NAME);

  return { workbook: previewWorkbook, xlsxBytes };
};

const getMergeMap = (sheet: XLSX.WorkSheet) => {
  const starts = new Map<string, number>();
  const covered = new Set<string>();
  const range = XLSX.utils.decode_range(sheet['!ref'] || EXPORT_RANGE);

  (sheet['!merges'] || []).forEach((merge) => {
    if (merge.s.r > range.e.r || merge.s.c > range.e.c) return;
    const startKey = `${merge.s.r}:${merge.s.c}`;
    starts.set(startKey, Math.min(4, merge.e.c) - merge.s.c + 1);

    for (let rowIndex = merge.s.r; rowIndex <= merge.e.r; rowIndex++) {
      for (let columnIndex = merge.s.c; columnIndex <= Math.min(merge.e.c, range.e.c); columnIndex++) {
        if (rowIndex === merge.s.r && columnIndex === merge.s.c) continue;
        covered.add(`${rowIndex}:${columnIndex}`);
      }
    }
  });

  return { starts, covered };
};

const getPreviewCellClassName = (sheet: XLSX.WorkSheet, rowIndex: number, colSpan: number) => {
  const range = XLSX.utils.decode_range(sheet['!ref'] || EXPORT_RANGE);
  const footerStart = Math.max(DATA_START_ROW_INDEX + 1, range.e.r - 11);
  const tableBorder = rowIndex >= DATA_START_ROW_INDEX && rowIndex < footerStart
    ? 'border border-slate-300'
    : '';

  if (rowIndex === 0) return `${tableBorder} bg-[#193767] text-white text-center text-[14px] font-semibold`;
  if (rowIndex === 2) return `${tableBorder} bg-slate-100 text-center font-medium text-slate-900`;
  if (isCoAClassificationRow(sheet, rowIndex)) return `${tableBorder} bg-[#193767] text-white text-center text-[13px] font-semibold`;
  if (isCoATableHeaderRow(sheet, rowIndex)) return `${tableBorder} bg-slate-700 text-white font-semibold`;
  if (rowIndex >= footerStart && rowIndex <= footerStart + 4 && colSpan > 1) return `${tableBorder} bg-white text-slate-900 leading-6`;
  if (rowIndex >= footerStart + 6) return `${tableBorder} bg-white font-medium text-slate-900`;
  return `${tableBorder} bg-white text-slate-900`;
};

const buildPreviewRows = (workbook: XLSX.WorkBook): PreviewRow[] => {
  const sheet = workbook.Sheets[COA_TEMPLATE_SHEET_NAME];
  const range = XLSX.utils.decode_range(sheet['!ref'] || EXPORT_RANGE);
  const mergeMap = getMergeMap(sheet);
  const rows: PreviewRow[] = [];

  for (let rowIndex = range.s.r; rowIndex <= range.e.r; rowIndex++) {
    const cells: PreviewCell[] = [];

    for (let columnIndex = range.s.c; columnIndex <= range.e.c; columnIndex++) {
      const key = `${rowIndex}:${columnIndex}`;
      if (mergeMap.covered.has(key)) continue;

      const colSpan = mergeMap.starts.get(key) || 1;
      cells.push({
        key,
        value: getCellDisplayText(sheet, rowIndex, columnIndex),
        colSpan,
        className: getPreviewCellClassName(sheet, rowIndex, colSpan),
      });
    }

    rows.push({ key: `row-${rowIndex}`, cells });
  }

  return rows;
};

const makeSafeFilePart = (value: string) =>
  cleanText(value).replace(/[\\/:*?"<>|]+/g, '_').replace(/\s+/g, '_') || 'release_task';

export default function ReleaseTaskExport({ batchNumber, comparisonResults, disabled = false }: ReleaseTaskExportProps) {
  const [previewWorkbook, setPreviewWorkbook] = useState<XLSX.WorkBook | null>(null);
  const [previewBytes, setPreviewBytes] = useState<Uint8Array | null>(null);
  const [previewRows, setPreviewRows] = useState<PreviewRow[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState('');

  const loadTemplateForPreview = async () => {
    const normalizedBatch = cleanText(batchNumber);
    if (!normalizedBatch) {
      setErrorText('Batch Number is required.');
      return;
    }

    setLoading(true);
    setErrorText('');
    try {
      const templateParams = new URLSearchParams();
      templateParams.set('range', '[0,999]');
      templateParams.set('perPage', '1000');
      templateParams.set('filter', JSON.stringify({ batchNumber: normalizedBatch }));

      const [templates, compareRows] = await Promise.all([
        getCOATemplates({
          range: templateParams.get('range') || '[0,999]',
          perPage: templateParams.get('perPage') || '1000',
          filter: templateParams.get('filter') || '',
        }),
        getReleaseCompareRowsByBatch(normalizedBatch),
      ]);

      const templateMeta =
        templates.find((item) => cleanText(item.batchNumber) === normalizedBatch) ||
        null;

      if (!templateMeta) {
        throw new Error(`No COA template found for batch ${normalizedBatch}.`);
      }

      const exactTemplate = await getCOATemplateById(templateMeta.id || '');

      const exportData = await buildCoATemplateWorkbook(exactTemplate, compareRows, comparisonResults);
      setPreviewWorkbook(exportData.workbook);
      setPreviewBytes(exportData.xlsxBytes);
      setPreviewRows(buildPreviewRows(exportData.workbook));
      setPreviewOpen(true);
    } catch (error) {
      setErrorText(error instanceof Error ? error.message : 'Load export preview failed.');
      setPreviewWorkbook(null);
      setPreviewBytes(null);
      setPreviewRows([]);
      setPreviewOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmExport = () => {
    if (!previewBytes) return;

    // Compute SHA-256 of preview bytes for integrity check (human-verifiable)
    const computeHashHex = async (bytes: Uint8Array) => {
      const buffer = bytes.buffer instanceof ArrayBuffer ? bytes.buffer : new Uint8Array(bytes).buffer;
      const digest = await crypto.subtle.digest('SHA-256', buffer as ArrayBuffer);
      return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
    };

    const triggerDownload = (bytes: Uint8Array, hashHex?: string) => {
      const arrayBuffer = new ArrayBuffer(bytes.byteLength);
      new Uint8Array(arrayBuffer).set(bytes);
      const blob = new Blob([arrayBuffer], { type: XLSX_MIME_TYPE });
      const link = document.createElement('a');
      const objectUrl = URL.createObjectURL(blob);
      link.href = objectUrl;
      link.download = `release_task_${makeSafeFilePart(batchNumber)}_coa_template.xlsx`;
      if (hashHex) {
        (link as any).dataset = { sha256: hashHex };
      }
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 30000);
    };

    void (async () => {
      try {
        const hashHex = await computeHashHex(previewBytes);
        // Log the hash so the exported file can be verified externally if needed
        console.log('Export preview SHA-256:', hashHex);

        triggerDownload(previewBytes, hashHex);
      } catch (err) {
        console.error('Failed to compute export hash', err);
        triggerDownload(previewBytes);
      }
    })();
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        disabled={disabled || loading}
        onClick={() => { void loadTemplateForPreview(); }}
      >
        <FileSpreadsheet className="size-4 mr-2" />
        {loading ? 'Loading...' : 'Export'}
      </Button>

      {errorText && (
        <span className="max-w-[260px] text-xs text-red-600">{errorText}</span>
      )}

      {previewOpen && previewWorkbook && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="flex max-h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-1.5">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Export Preview</h3>
                <p className="text-sm text-gray-500">CoA Template - Batch Number: {cleanText(batchNumber) || '-'}</p>
              </div>
              <Button type="button" variant="ghost" size="sm" onClick={() => setPreviewOpen(false)} title="Close">
                <X className="size-3.5" />
              </Button>
            </div>

            <div className="overflow-auto p-5">
              <table className="w-full min-w-[980px] border border-slate-300 border-collapse table-fixed text-sm">
                <colgroup>
                  <col className="w-[33.3%]" />
                  <col className="w-[17.6%]" />
                  <col className="w-[17.6%]" />
                  <col className="w-[31.5%]" />
                </colgroup>
                <tbody>
                  {previewRows.map((row) => (
                    <tr key={row.key}>
                      {row.cells.map((cell) => (
                        <td
                          key={cell.key}
                          colSpan={cell.colSpan}
                          className={`px-2 py-1.5 align-top whitespace-pre-line break-words ${cell.className}`}
                        >
                          {cell.value || ''}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-5 py-4">
              <Button type="button" variant="outline" onClick={() => setPreviewOpen(false)}>
                Close
              </Button>
              <Button type="button" onClick={handleConfirmExport}>
                <Download className="size-4 mr-2" />
                Confirm Export
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
